import { useState, useCallback } from "react";
import {
  Building2,
  MapPin,
  Receipt,
  Landmark,
  Users,
  UserCheck,
  TrendingUp,
  FileText,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Fingerprint,
  ShieldCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DiscoverySection, EditableField } from "./DiscoverySection";
import { DiscoverySummaryCard } from "./DiscoverySummaryCard";
import { DiscoveryChecklist } from "./DiscoveryChecklist";
import { ConflictsSummaryPanel } from "./ConflictsSummaryPanel";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { ConflictIndicator } from "./ConflictIndicator";
import { EntityFeedbackButton } from "./EntityFeedbackButton";
import { CorrectionPanel } from "./CorrectionPanel";
import { TransactionCategorizationPanel } from "./TransactionCategorizationPanel";
import { SourceBadgeInline } from "./SourceAttributionBadge";
import { IndustryTemplateSelector } from "./IndustryTemplateSelector";
import type {
  ExtractedCompanyProfile,
  DataConflict,
  SourceDocument,
  CensusFirstPipelineSummary,
} from "@shared/discovery-types";
import { cn } from "@/lib/utils";

interface CompanyDiscoveryPreviewProps {
  profile: ExtractedCompanyProfile;
  documents: SourceDocument[];
  conflicts: DataConflict[];
  onUpdateField: (path: string, value: string) => void;
  onResolveConflict: (field: string, selectedIndex: number) => void;
  /** Callback when a transaction category is changed */
  onCategorizeTransaction?: (
    transactionId: string,
    accountCode: string,
    accountName: string
  ) => void;
  /** Callback for bulk transaction categorization */
  onBulkCategorizeTransactions?: (
    transactionIds: string[],
    accountCode: string,
    accountName: string
  ) => void;
  className?: string;
  /** Discovery session ID for refinement actions */
  sessionId?: string;
  /** Project name (fallback for company name in refinement actions) */
  projectName?: string;
  /** Callback when refinement completes - refresh data */
  onRefineComplete?: () => void;
  /** Whether extraction completed partially (Census only, holistic timed out) */
  partialExtraction?: boolean;
  /** Reason for partial extraction */
  partialReason?: string;
  /** Census-First pipeline result (when feature flag is enabled) */
  censusFirstResult?: CensusFirstPipelineSummary;
  /** Selected industry template slug */
  industryTemplateSlug?: string;
  /** Callback when industry template changes */
  onIndustryTemplateChange?: (slug: string) => void;
  /** Company ID — when set, enables classification corrections (learning loop) */
  companyId?: number;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
  }).format(cents / 100);
}

export function CompanyDiscoveryPreview({
  profile,
  documents,
  conflicts,
  onUpdateField,
  onResolveConflict,
  onCategorizeTransaction,
  onBulkCategorizeTransactions,
  className,
  sessionId,
  projectName,
  onRefineComplete,
  partialExtraction,
  partialReason,
  censusFirstResult,
  industryTemplateSlug,
  onIndustryTemplateChange,
  companyId,
}: CompanyDiscoveryPreviewProps) {
  // Correction panel state
  const [correctionTarget, setCorrectionTarget] = useState<{
    entityType: "vendor" | "transaction" | "document";
    entityId: number;
    entityDescription?: string;
    currentCategory?: string;
  } | null>(null);

  // Progressive disclosure: start with summary, sections collapsed
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    company: false,
    address: false,
    tax: false,
    bankAccounts: false,
    vendors: false,
    customers: false,
    financial: false,
    documents: false,
  });
  const [showAllDetails, setShowAllDetails] = useState(false);

  const getConflict = (field: string) => conflicts.find(c => c.field === field);

  const {
    companyInfo,
    taxInfo,
    bankAccounts,
    vendors,
    customers,
    financialSummary,
  } = profile;

  // Calculate attention counts for each section
  const companyConflicts = conflicts.filter(c =>
    c.field.startsWith("companyInfo.")
  ).length;
  const taxConflicts = conflicts.filter(c =>
    c.field.startsWith("taxInfo.")
  ).length;
  const bankAccountsLowConfidence =
    bankAccounts?.filter(a => a.confidence < 70).length || 0;
  const vendorsLowConfidence =
    vendors?.filter(v => v.confidence < 70).length || 0;
  const customersLowConfidence =
    customers?.filter(c => c.confidence < 70).length || 0;

  // Determine section status
  const getSectionStatus = (
    hasData: boolean,
    attentionCount: number,
    confidence?: number
  ): "confirmed" | "attention" | "empty" => {
    if (!hasData) return "empty";
    if (attentionCount > 0) return "attention";
    if (confidence !== undefined && confidence < 70) return "attention";
    return "confirmed";
  };

  const companyStatus = getSectionStatus(
    Boolean(companyInfo?.name),
    companyConflicts,
    companyInfo?.confidence
  );
  const addressStatus = getSectionStatus(
    Boolean(companyInfo?.address?.city),
    conflicts.filter(c => c.field.includes("address")).length
  );
  const taxStatus = getSectionStatus(
    Boolean(taxInfo?.canton),
    taxConflicts,
    taxInfo?.confidence
  );
  const bankAccountsStatus = getSectionStatus(
    (bankAccounts?.length || 0) > 0,
    bankAccountsLowConfidence
  );
  const vendorsStatus = getSectionStatus(
    (vendors?.length || 0) > 0,
    vendorsLowConfidence
  );
  const customersStatus = getSectionStatus(
    (customers?.length || 0) > 0,
    customersLowConfidence
  );

  // Handle section toggle
  const handleSectionToggle = useCallback(
    (section: string, expanded: boolean) => {
      setExpandedSections(prev => ({ ...prev, [section]: expanded }));
    },
    []
  );

  // Expand all sections
  const handleExpandAll = useCallback(() => {
    setShowAllDetails(true);
    setExpandedSections({
      company: true,
      address: true,
      tax: true,
      bankAccounts: true,
      vendors: true,
      customers: true,
      financial: true,
      documents: true,
    });
  }, []);

  // Collapse all sections
  const handleCollapseAll = useCallback(() => {
    setShowAllDetails(false);
    setExpandedSections({
      company: false,
      address: false,
      tax: false,
      bankAccounts: false,
      vendors: false,
      customers: false,
      financial: false,
      documents: false,
    });
  }, []);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Card - Always visible */}
      <DiscoverySummaryCard
        profile={profile}
        conflicts={conflicts}
        onExpandAll={handleExpandAll}
      />

      {/* Living Checklist - Shows extraction status and readiness */}
      <DiscoveryChecklist profile={profile} conflicts={conflicts} />

      {/* Conflicts Panel - Prominent display when conflicts exist */}
      {conflicts.length > 0 && (
        <ConflictsSummaryPanel
          conflicts={conflicts}
          onResolveConflict={onResolveConflict}
        />
      )}

      {/* Partial Extraction Notice */}
      {partialExtraction && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-[var(--warning)]/30 bg-warning/10">
          <AlertCircle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-warning">
              Quick Analysis Complete
            </p>
            <p className="text-sm text-muted-foreground">
              Basic company profile extracted successfully. Detailed
              vendor/customer analysis was skipped to prevent timeout. You can
              still create the company with the data shown below.
            </p>
            {partialReason && (
              <p className="text-xs text-muted-foreground/70 mt-1">
                Technical: {partialReason}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Census-First Quality Metrics */}
      {censusFirstResult && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Fingerprint className="h-5 w-5 text-primary" />
            <h3 className="font-medium">Census-First Pipeline Results</h3>
            <Badge
              variant="outline"
              className={cn(
                "ml-auto",
                censusFirstResult.quality.grade === "A" &&
                  "border-[var(--success)] text-success",
                censusFirstResult.quality.grade === "B" &&
                  "border-primary text-primary",
                censusFirstResult.quality.grade === "C" &&
                  "border-[var(--warning)] text-warning",
                (censusFirstResult.quality.grade === "D" ||
                  censusFirstResult.quality.grade === "F") &&
                  "border-destructive text-destructive"
              )}
            >
              Grade {censusFirstResult.quality.grade}
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Quality Score</div>
              <div className="font-medium">
                {censusFirstResult.quality.overall}%
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">
                Validated Facts
              </div>
              <div className="font-medium">
                {censusFirstResult.quality.validatedFacts}/
                {censusFirstResult.quality.totalFacts}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">
                Definitive Sources
              </div>
              <div className="font-medium flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5 text-success" />
                {censusFirstResult.summary.definitiveSourcesFound}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Coverage</div>
              <div className="font-medium">
                {censusFirstResult.coverage.overallCompleteness}%
              </div>
            </div>
          </div>

          {censusFirstResult.summary.gapsIdentified > 0 && (
            <div className="mt-3 pt-3 border-t text-sm">
              <div className="flex items-center gap-2 text-warning">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {censusFirstResult.summary.gapsIdentified} coverage gap
                  {censusFirstResult.summary.gapsIdentified !== 1
                    ? "s"
                    : ""}{" "}
                  identified
                </span>
              </div>
            </div>
          )}

          {censusFirstResult.coverage.fiscalYears.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              Fiscal years covered:{" "}
              {censusFirstResult.coverage.fiscalYears
                .sort((a, b) => b - a)
                .join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Expand/Collapse All Toggle */}
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={showAllDetails ? handleCollapseAll : handleExpandAll}
          className="text-muted-foreground"
        >
          {showAllDetails ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Collapse all
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Expand all
            </>
          )}
        </Button>
      </div>

      {/* Company Information */}
      <DiscoverySection
        title="Company Information"
        icon={<Building2 className="h-5 w-5" />}
        confidence={companyInfo?.confidence}
        expanded={expandedSections.company}
        onExpandedChange={exp => handleSectionToggle("company", exp)}
        defaultExpanded={false}
        status={companyStatus}
        attentionCount={companyConflicts}
        sectionType="company"
        sessionId={sessionId}
        companyName={companyInfo?.name || projectName}
        onRefineComplete={onRefineComplete}
        headerExtra={
          companyInfo?.sources && companyInfo.sources.length > 0 ? (
            <SourceBadgeInline sources={companyInfo.sources} />
          ) : undefined
        }
      >
        <div className="space-y-1 pt-3">
          <div className="flex items-center justify-between group">
            <EditableField
              label="Company Name"
              value={companyInfo?.name}
              path="companyInfo.name"
              onUpdate={onUpdateField}
              className="flex-1"
            />
            {getConflict("companyInfo.name") && (
              <ConflictIndicator
                conflict={getConflict("companyInfo.name")!}
                onResolve={idx => onResolveConflict("companyInfo.name", idx)}
              />
            )}
          </div>

          <div className="flex items-center justify-between group">
            <EditableField
              label="Swiss UID"
              value={companyInfo?.uid}
              path="companyInfo.uid"
              onUpdate={onUpdateField}
              placeholder="CHE-XXX.XXX.XXX"
              className="flex-1"
            />
            {getConflict("companyInfo.uid") && (
              <ConflictIndicator
                conflict={getConflict("companyInfo.uid")!}
                onResolve={idx => onResolveConflict("companyInfo.uid", idx)}
              />
            )}
          </div>

          <EditableField
            label="Legal Form"
            value={companyInfo?.legalForm}
            path="companyInfo.legalForm"
            onUpdate={onUpdateField}
          />

          <EditableField
            label="Industry"
            value={companyInfo?.industry}
            path="companyInfo.industry"
            onUpdate={onUpdateField}
          />
        </div>
      </DiscoverySection>

      {/* Address */}
      <DiscoverySection
        title="Address"
        icon={<MapPin className="h-5 w-5" />}
        expanded={expandedSections.address}
        onExpandedChange={exp => handleSectionToggle("address", exp)}
        defaultExpanded={false}
        status={addressStatus}
        sectionType="address"
        sessionId={sessionId}
        onRefineComplete={onRefineComplete}
      >
        <div className="space-y-1 pt-3">
          <EditableField
            label="Street"
            value={companyInfo?.address?.street}
            path="companyInfo.address.street"
            onUpdate={onUpdateField}
          />

          <div className="flex gap-4">
            <EditableField
              label="ZIP"
              value={companyInfo?.address?.zip}
              path="companyInfo.address.zip"
              onUpdate={onUpdateField}
              className="w-32"
            />
            <EditableField
              label="City"
              value={companyInfo?.address?.city}
              path="companyInfo.address.city"
              onUpdate={onUpdateField}
              className="flex-1"
            />
          </div>

          <div className="flex items-center justify-between group">
            <EditableField
              label="Canton"
              value={companyInfo?.address?.canton}
              path="companyInfo.address.canton"
              onUpdate={onUpdateField}
              className="flex-1"
            />
            {getConflict("companyInfo.address.canton") && (
              <ConflictIndicator
                conflict={getConflict("companyInfo.address.canton")!}
                onResolve={idx =>
                  onResolveConflict("companyInfo.address.canton", idx)
                }
              />
            )}
          </div>

          <EditableField
            label="Country"
            value={companyInfo?.address?.country || "CH"}
            path="companyInfo.address.country"
            onUpdate={onUpdateField}
          />
        </div>
      </DiscoverySection>

      {/* Tax Information */}
      <DiscoverySection
        title="Tax & Jurisdiction"
        icon={<Receipt className="h-5 w-5" />}
        confidence={taxInfo?.confidence}
        expanded={expandedSections.tax}
        onExpandedChange={exp => handleSectionToggle("tax", exp)}
        defaultExpanded={false}
        status={taxStatus}
        attentionCount={taxConflicts}
        sectionType="tax"
        sessionId={sessionId}
        onRefineComplete={onRefineComplete}
        headerExtra={
          taxInfo?.sources && taxInfo.sources.length > 0 ? (
            <SourceBadgeInline sources={taxInfo.sources} />
          ) : undefined
        }
      >
        <div className="space-y-1 pt-3">
          <div className="flex items-center justify-between group">
            <EditableField
              label="Tax Canton"
              value={taxInfo?.canton}
              path="taxInfo.canton"
              onUpdate={onUpdateField}
              className="flex-1"
            />
            {getConflict("taxInfo.canton") && (
              <ConflictIndicator
                conflict={getConflict("taxInfo.canton")!}
                onResolve={idx => onResolveConflict("taxInfo.canton", idx)}
              />
            )}
          </div>

          <EditableField
            label="VAT Number"
            value={taxInfo?.vatNumber}
            path="taxInfo.vatNumber"
            onUpdate={onUpdateField}
          />

          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-muted-foreground">
              MWST Registered
            </span>
            <Badge variant={taxInfo?.mwstRegistered ? "default" : "secondary"}>
              {taxInfo?.mwstRegistered ? "Yes" : "No / Unknown"}
            </Badge>
          </div>

          {taxInfo?.lossCarryforward && (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Loss Carryforward
              </span>
              <span className="text-sm font-medium">
                {formatCurrency(taxInfo.lossCarryforward)}
              </span>
            </div>
          )}
        </div>
      </DiscoverySection>

      {/* Industry Template Selector */}
      {onIndustryTemplateChange && (
        <IndustryTemplateSelector
          value={industryTemplateSlug || ""}
          onChange={onIndustryTemplateChange}
        />
      )}

      {/* Bank Accounts */}
      <DiscoverySection
        title={`Bank Accounts (${bankAccounts?.length || 0})`}
        icon={<Landmark className="h-5 w-5" />}
        expanded={expandedSections.bankAccounts}
        onExpandedChange={exp => handleSectionToggle("bankAccounts", exp)}
        defaultExpanded={false}
        status={bankAccountsStatus}
        attentionCount={bankAccountsLowConfidence}
        sectionType="bankAccounts"
        sessionId={sessionId}
        onRefineComplete={onRefineComplete}
      >
        {bankAccounts && bankAccounts.length > 0 ? (
          <div className="space-y-3 pt-3">
            {bankAccounts.map((account, index) => (
              <div
                key={account.iban || index}
                className="p-3 rounded-lg border bg-muted/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">
                    {account.bank ||
                      account.accountName ||
                      `Account ${index + 1}`}
                  </span>
                  <ConfidenceBadge confidence={account.confidence} size="sm" />
                </div>
                {account.iban && (
                  <div className="text-sm font-mono text-muted-foreground mb-1">
                    {account.iban}
                  </div>
                )}
                <div className="flex gap-4 text-xs text-muted-foreground">
                  {account.currency && <span>{account.currency}</span>}
                  {account.transactionCount && (
                    <span>{account.transactionCount} transactions</span>
                  )}
                  {account.dateRange && (
                    <span>
                      {account.dateRange.from} - {account.dateRange.to}
                    </span>
                  )}
                </div>
                {(account.totalCredits || account.totalDebits) && (
                  <div className="flex gap-4 text-sm mt-2">
                    {account.totalCredits && (
                      <span className="text-success">
                        + {formatCurrency(account.totalCredits)}
                      </span>
                    )}
                    {account.totalDebits && (
                      <span className="text-destructive">
                        - {formatCurrency(account.totalDebits)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pt-3 italic">
            No bank accounts discovered. Upload bank statements to extract
            account information.
          </p>
        )}
      </DiscoverySection>

      {/* Vendors (Swiss and Global) */}
      <DiscoverySection
        title={`Vendors (${vendors?.length || 0})`}
        icon={<Users className="h-5 w-5" />}
        expanded={expandedSections.vendors}
        onExpandedChange={exp => handleSectionToggle("vendors", exp)}
        defaultExpanded={false}
        status={vendorsStatus}
        attentionCount={vendorsLowConfidence}
        sectionType="vendors"
        sessionId={sessionId}
        onRefineComplete={onRefineComplete}
      >
        {vendors && vendors.length > 0 ? (
          <div className="space-y-2 pt-3">
            <div className="text-xs text-muted-foreground mb-2">
              Top vendors by transaction volume
            </div>
            {vendors.slice(0, 10).map((vendor, index) => (
              <div
                key={vendor.normalizedName || index}
                className="flex items-center justify-between py-2 border-b last:border-0 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{vendor.name}</span>
                    {vendor.uid && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        CHE verified
                      </Badge>
                    )}
                    {vendor.category && (
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {vendor.category}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {vendor.transactionCount} transaction
                    {vendor.transactionCount !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <div className="text-right">
                    <div className="font-medium">
                      {formatCurrency(vendor.totalAmount)}
                    </div>
                    <ConfidenceBadge
                      confidence={vendor.confidence}
                      size="sm"
                      showLabel={false}
                    />
                  </div>
                  {sessionId && (
                    <EntityFeedbackButton
                      sessionId={parseInt(sessionId, 10)}
                      entityType="vendor"
                      entityName={vendor.name}
                      entityUid={vendor.uid}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onFeedbackSubmitted={onRefineComplete}
                    />
                  )}
                  {companyId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                      onClick={() =>
                        setCorrectionTarget({
                          entityType: "vendor",
                          entityId: index + 1,
                          entityDescription: vendor.name,
                          currentCategory: vendor.classificationType,
                        })
                      }
                    >
                      Correct
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {vendors.length > 10 && (
              <p className="text-sm text-muted-foreground text-center pt-2">
                + {vendors.length - 10} more vendors
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pt-3 italic">
            No vendors discovered. Upload bank statements to extract vendor
            information.
          </p>
        )}
      </DiscoverySection>

      {/* Customers */}
      <DiscoverySection
        title={`Customers (${customers?.length || 0})`}
        icon={<UserCheck className="h-5 w-5" />}
        expanded={expandedSections.customers}
        onExpandedChange={exp => handleSectionToggle("customers", exp)}
        defaultExpanded={false}
        status={customersStatus}
        attentionCount={customersLowConfidence}
        sectionType="customers"
        sessionId={sessionId}
        onRefineComplete={onRefineComplete}
      >
        {customers && customers.length > 0 ? (
          <div className="space-y-2 pt-3">
            <div className="text-xs text-muted-foreground mb-2">
              Top customers by revenue
            </div>
            {customers.slice(0, 10).map((customer, index) => (
              <div
                key={customer.normalizedName || index}
                className="flex items-center justify-between py-2 border-b last:border-0 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {customer.name}
                    </span>
                    {customer.customerType &&
                      customer.customerType !== "unknown" && (
                        <Badge
                          variant="secondary"
                          className="text-xs shrink-0 capitalize"
                        >
                          {customer.customerType}
                        </Badge>
                      )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {customer.transactionCount} transaction
                    {customer.transactionCount !== 1 ? "s" : ""}
                    {customer.country && ` • ${customer.country}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <div className="text-right">
                    <div className="font-medium text-success">
                      {formatCurrency(customer.totalRevenue)}
                    </div>
                    <ConfidenceBadge
                      confidence={customer.confidence}
                      size="sm"
                      showLabel={false}
                    />
                  </div>
                  {sessionId && (
                    <EntityFeedbackButton
                      sessionId={parseInt(sessionId, 10)}
                      entityType="customer"
                      entityName={customer.name}
                      entityUid={customer.uid}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      onFeedbackSubmitted={onRefineComplete}
                    />
                  )}
                </div>
              </div>
            ))}
            {customers.length > 10 && (
              <p className="text-sm text-muted-foreground text-center pt-2">
                + {customers.length - 10} more customers
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pt-3 italic">
            No customers discovered. Upload invoices or bank statements to
            extract customer information.
          </p>
        )}
      </DiscoverySection>

      {/* Transaction Categorization */}
      {profile.transactions &&
        profile.transactions.length > 0 &&
        onCategorizeTransaction && (
          <TransactionCategorizationPanel
            transactions={profile.transactions}
            onCategorize={onCategorizeTransaction}
            onBulkCategorize={onBulkCategorizeTransactions}
          />
        )}

      {/* Financial Summary */}
      {financialSummary && (
        <DiscoverySection
          title="Financial Summary"
          icon={<TrendingUp className="h-5 w-5" />}
          confidence={financialSummary.confidence}
          expanded={expandedSections.financial}
          onExpandedChange={exp => handleSectionToggle("financial", exp)}
          defaultExpanded={false}
          status={financialSummary.confidence >= 70 ? "confirmed" : "attention"}
        >
          <div className="space-y-3 pt-3">
            {financialSummary.fiscalYears?.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Fiscal Years:
                </span>
                <div className="flex gap-1">
                  {financialSummary.fiscalYears.slice(0, 5).map(year => (
                    <Badge key={year} variant="outline">
                      {year}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {financialSummary.latestRevenue && (
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">Revenue</div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(financialSummary.latestRevenue)}
                  </div>
                </div>
              )}

              {financialSummary.latestNetIncome && (
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">
                    Net Income
                  </div>
                  <div
                    className={cn(
                      "text-lg font-semibold",
                      financialSummary.latestNetIncome >= 0
                        ? "text-success"
                        : "text-destructive"
                    )}
                  >
                    {formatCurrency(financialSummary.latestNetIncome)}
                  </div>
                </div>
              )}

              {financialSummary.totalAssets && (
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">
                    Total Assets
                  </div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(financialSummary.totalAssets)}
                  </div>
                </div>
              )}

              {financialSummary.totalEquity && (
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="text-xs text-muted-foreground">
                    Total Equity
                  </div>
                  <div className="text-lg font-semibold">
                    {formatCurrency(financialSummary.totalEquity)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DiscoverySection>
      )}

      {/* Source Documents */}
      <DiscoverySection
        title={`Source Documents (${documents?.length || 0})`}
        icon={<FileText className="h-5 w-5" />}
        expanded={expandedSections.documents}
        onExpandedChange={exp => handleSectionToggle("documents", exp)}
        defaultExpanded={false}
        status={(documents?.length || 0) > 0 ? "confirmed" : "empty"}
      >
        {documents && documents.length > 0 ? (
          <div className="space-y-2 pt-3">
            {documents.map(doc => (
              <div
                key={doc.id}
                className="flex items-center justify-between py-2 border-b last:border-0"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm truncate max-w-[200px]">
                    {doc.name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {doc.classification.type.replace(/_/g, " ")}
                  </Badge>
                  <Badge
                    variant={
                      doc.processingStatus === "completed"
                        ? "default"
                        : doc.processingStatus === "failed"
                          ? "destructive"
                          : "secondary"
                    }
                    className="text-xs capitalize"
                  >
                    {doc.processingStatus}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground pt-3 italic">
            No documents processed yet.
          </p>
        )}
      </DiscoverySection>

      {/* Correction Panel for learning loop */}
      {companyId && correctionTarget && (
        <CorrectionPanel
          open={!!correctionTarget}
          onOpenChange={open => {
            if (!open) setCorrectionTarget(null);
          }}
          companyId={companyId}
          entityType={correctionTarget.entityType}
          entityId={correctionTarget.entityId}
          entityDescription={correctionTarget.entityDescription}
          currentCategory={correctionTarget.currentCategory}
          onCorrectionSubmitted={() => {
            setCorrectionTarget(null);
            onRefineComplete?.();
          }}
        />
      )}
    </div>
  );
}
